import { DRINK_EXTRAS, type Per100g } from '@gramas/shared'
import { List, ListRow, SectionLabel, Stepper } from '@gramas/ui'

/** Extra key → how many spoons or splashes of it went in. */
export type Extras = Map<string, number>

export function setExtraCount(previous: Extras, key: string, next: number): Extras {
  const out = new Map(previous)
  if (next <= 0) out.delete(key)
  else out.set(key, Math.min(20, next))
  return out
}

/** The chosen extras as macro lines, ready to add to whatever they went into. */
export function extraLines(
  extras: Extras,
  multiplier = 1,
): Array<{ per100g: Per100g; unitWeightG: number; quantity: number }> {
  const lines: Array<{ per100g: Per100g; unitWeightG: number; quantity: number }> = []
  for (const extra of DRINK_EXTRAS) {
    const count = extras.get(extra.key) ?? 0
    if (count <= 0) continue
    lines.push({
      per100g: extra.per100g,
      unitWeightG: extra.unitG * count * multiplier,
      quantity: 1,
    })
  }
  return lines
}

/** " + cukrus ×2, pienas" — what was stirred in, in the diary's own words. */
export function extraSummary(extras: Extras): string {
  const named: string[] = []
  for (const extra of DRINK_EXTRAS) {
    const count = extras.get(extra.key) ?? 0
    if (count <= 0) continue
    const short = extra.name.replace(/\s*\(.*\)$/, '').toLowerCase()
    named.push(count > 1 ? `${short} ×${count}` : short)
  }
  return named.length === 0 ? '' : ` + ${named.join(', ')}`
}

/**
 * What went into the cup after the counter handed it over.
 *
 * Offered on anything drunk, from any chain and from the catalogue alike,
 * because two sugars in a coffee are 32 kcal whoever poured the coffee — and
 * they are invisible to every published figure there is.
 */
export function DrinkExtrasSection({
  extras,
  onChange,
  note,
}: {
  extras: Extras
  onChange: (next: Extras) => void
  note?: string
}) {
  return (
    <section>
      <SectionLabel>Priedai</SectionLabel>
      <List className="border-y border-line bg-surface">
        {DRINK_EXTRAS.map((extra, i) => {
          const count = extras.get(extra.key) ?? 0
          return (
            <ListRow
              key={extra.key}
              last={i === DRINK_EXTRAS.length - 1}
              title={extra.name}
              value={`${Math.round((extra.per100g.kcal * extra.unitG * Math.max(1, count)) / 100)}`}
              meta="kcal"
              className={count > 0 ? 'bg-raised' : undefined}
              accessory={
                <Stepper
                  value={count}
                  onChange={(next) => onChange(setExtraCount(extras, extra.key, next))}
                />
              }
            />
          )
        })}
      </List>
      <p className="px-4 py-2 text-xs text-mute">
        {note ?? 'Įprastos vertės — šaukštelis, lašas.'}
      </p>
    </section>
  )
}
